import XUILoader from '@xero/xui/react/loader';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHeaderData } from './actions';
import './App.css';
import { removeHeader, setupHeader, setVisible } from './helpers/header';
import { Pages } from './helpers/routes';
import { FetchError, IOrganisation } from './models';
import Fatality from './pages/Fatality';
import { bootstrapSelectors, getBootstrapAction } from './redux/slices/BootstrapSlice';
import { connectionSelectors, postConnectionAction } from './redux/slices/ConnectionSlice';
import {
    deleteInstallationAction,
    installationSelectors,
    postInstallationAction,
    updateInstallationVersionAction,
} from './redux/slices/InstallationSlice';
import { organisationSelectors, setOrganisation } from './redux/slices/OrganisationSlice';
import { updateUser, userSelectors } from './redux/slices/UserSlice';
import { useAppDispatch } from './redux/store';
import { beanieService } from './services/beanie/shopify';
import { connectionTypes, installationStates, integrationTypes, localStorageKeys } from './static/constants';
import lang from './static/lang/en-NZ';
import { LDClient } from 'launchdarkly-js-client-sdk';
import ErrorComponent from './components/ErrorComponent/ErrorComponent';
import Link from './components/Link';
import React from 'react';

export interface IAppProps {
    ldClient?: LDClient;
}

const App: FC<IAppProps> = (props) => {
    const dispatch = useAppDispatch();

    const isAllowed = useSelector(bootstrapSelectors.isAllowed);
    const isBootstrapping = useSelector(bootstrapSelectors.isBootstrapping);
    const bootstrapError = useSelector(bootstrapSelectors.error);
    const isFetching = useSelector(installationSelectors.isFetching);
    const installation = useSelector(installationSelectors.installation);
    const routes = useSelector(installationSelectors.routes);
    const oAuthCode = useSelector(connectionSelectors.oAuthCode);
    const isConnecting = useSelector(connectionSelectors.isConnecting);
    const user = useSelector(userSelectors.user);
    const organisation = useSelector(organisationSelectors.organisation);

    const [isEditingSettings, setIsEditingSettings] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isUpdatingTenant, setIsUpdatingTenant] = useState(false);
    const [isValidatingOrg, setIsValidatingOrg] = useState(false);
    const [showDisconnectedBanner, setShowDisconnectedBanner] = useState(false);
    const ldClient = props.ldClient!; // this should be defined by the call to withLDConsumer which wraps this component
    const loadedInitHeader = useRef(false);
    const authCodeReference = useRef<string | undefined>('initial');

    const [skipWizard] = useState(
        window.localStorage.getItem(localStorageKeys.SKIP_WIZARD) !== null
            ? Boolean(window.localStorage.getItem(localStorageKeys.SKIP_WIZARD)!)
            : false
    );
    const [backendError, setBackendError] = useState<Error | undefined>();

    const initHeaderAsync = useCallback(async () => {
        try {
            const header = await getHeaderData(user);
            setupHeader(header);
        } catch (error) {
            // If we failed to get the header, don't crash the app
        }
    }, [user]);

    useEffect(() => {
        if (loadedInitHeader.current) {
            return;
        }

        loadedInitHeader.current = true;

        (async () => {
            dispatch(getBootstrapAction());
            await initHeaderAsync();
        })();
    }, [dispatch, initHeaderAsync]);

    const postConnection = useCallback(
        async (currentOrg: IOrganisation) => {
            await dispatch(postConnectionAction())
                .unwrap()
                .then(async (connectionResponse) => {
                    await beanieService.sendOutboundConnectionCreatedEvent(
                        installation.integrationType,
                        currentOrg.id,
                        user
                    );

                    if (connectionResponse.appRedirectUrl) {
                        window.location.href = connectionResponse.appRedirectUrl;
                        return;
                    }
                    setIsSyncing(true);
                })
                .catch((error) => {
                    setBackendError(error);
                });

            const url = user.tenantId
                ? `${window.location.href.split('?')[0]}?tenantId=${user.tenantId}`
                : window.location.href.split('?')[0];
            window.history.pushState({}, '', url);
        },
        [dispatch, installation.integrationType, user]
    );

    useEffect(() => {
        (async () => {
            if (authCodeReference.current === oAuthCode && authCodeReference.current != 'initial') {
                return;
            }

            authCodeReference.current = oAuthCode;

            // If they can't change their organisation, connect with their current organisation
            // As of 10/12/2021 only shopify has an org switcher applied to it therefore
            // any other integration type will post connection at this point
            if (oAuthCode && !routes!.get(Pages.Organisation)) {
                await postConnection(organisation);
                if (!backendError) {
                    dispatch(getBootstrapAction());
                    await initHeaderAsync();
                }
            }
        })();
    }, [oAuthCode, backendError, dispatch, initHeaderAsync, postConnection, routes, user, organisation]);

    useEffect(() => {
        setBackendError(bootstrapError);
    }, [bootstrapError]);

    useEffect(() => {
        (async () =>
            ldClient.identify({
                key: user.xero_userid,
                custom: { tenantId: organisation.id, tenantRegion: organisation.region! },
            }))();
    }, [organisation, ldClient, user.xero_userid]);

    const onConnectClick = async (authorizeUrl?: string) => {
        // An installation may already exist on an abandoned connect
        if (!installation.installationId) {
            await dispatch(postInstallationAction())
                .unwrap()
                .catch(async (error) => {
                    setBackendError(error);
                    return;
                });
        }

        await beanieService.sendOutboundConnectionStartedEvent(installation.integrationType, organisation.id, user);

        switch (installation.connectionType) {
            case connectionTypes.CUSTOM:
            case connectionTypes.OAUTH2:
                if (authorizeUrl) {
                    window.location.href = authorizeUrl;
                }
                break;
        }
    };

    const onPaidConnectClick = async () => {
        if (!installation.installationId) {
            await dispatch(postInstallationAction())
                .unwrap()
                .catch(async (error) => {
                    setBackendError(error);
                    return;
                });
        }

        await beanieService.sendOutboundConnectionStartedEvent(installation.integrationType, organisation.id, user);
        window.location.href = installation.billing?.url ?? '';
    };

    const onSetupComplete = (isInitialSetup: boolean) => {
        setIsEditingSettings(false);
        setIsSyncing(isInitialSetup);
    };

    const handleError = (error: any) => setBackendError(error);

    const getPage = (): Pages => {
        if (oAuthCode) {
            return Pages.Organisation;
        }

        if (
            !isValidatingOrg &&
            window.localStorage.getItem(localStorageKeys.CONTEXT_ORG) &&
            routes.get(Pages.Validator)
        ) {
            // if context org is set, it means the selected org is invalid and user will be redirected to
            // org switcher once we get oauth code from shopify, show loader while redirect is happening
            return Pages.Loader;
        }

        if (installation.state === installationStates.HEALTHY && isValidatingOrg) {
            return Pages.Validator;
        }

        if (
            !installation.state ||
            installation.state === installationStates.CONNECTION_REQUIRED ||
            (!installation.isOnboarded &&
                installation.billing?.hasPaidPlan &&
                !installation.billing?.hasFreePlan &&
                !installation.billing?.hasBought &&
                !installation.freeTrial?.isEligibleForTrial)
        ) {
            return Pages.Access;
        }

        if (
            installation.isOnboarded ||
            (installation.isUpgrading && installation.integrationType === integrationTypes.SHOPIFY)
        ) {
            return isEditingSettings ? Pages.Settings : Pages.Connected;
        }

        return isEditingSettings || !routes.get(Pages.Wizard) || skipWizard ? Pages.Setup : Pages.Wizard;
    };

    const renderPage = () => {
        const page = getPage();
        const Route = routes!.get(page)!;
        switch (page) {
            case Pages.Access:
                return (
                    <Route
                        onConnectClick={onConnectClick}
                        onPaidConnectClick={onPaidConnectClick}
                        showDisconnectedBanner={showDisconnectedBanner}
                    />
                );
            case Pages.Wizard:
                return <Route onWizardComplete={onSettingsClick} onWizardClose={onDeleteClick} />;
            case Pages.Setup:
                return <Route onSaveClick={onSetupComplete} onCancelClick={onDeleteClick} onError={handleError} />;
            case Pages.Connected:
                return (
                    <Route
                        onDisconnectClick={onDeleteClick}
                        onSettingsClick={onSettingsClick}
                        onUpdateAppClick={onUpdateAppClick}
                        isSyncing={isSyncing}
                        onPaidConnectClick={onPaidConnectClick}
                    />
                );
            case Pages.Settings:
                return <Route onSaveClick={onSetupComplete} onCancelClick={onSettingsCancel} />;
            case Pages.Organisation:
                return (
                    <Route
                        setOrganisation={updateTenant}
                        onClose={onOrgSwitcherCancelClick}
                        invalidOrgId={window.localStorage.getItem(localStorageKeys.CONTEXT_ORG)}
                    />
                );
            case Pages.Validator:
                return <Route setIsInvalidOrg={setIsInvalidOrg} />;
            default:
                return <XUILoader ariaLabel='Loading' />;
        }
    };

    const onDeleteClick = async (cancelSubscription: boolean = false) => {
        setVisible();
        await dispatch(deleteInstallationAction(cancelSubscription))
            .unwrap()
            .then(async () => {
                await dispatch(getBootstrapAction());
                setShowDisconnectedBanner(true);
                setIsInvalidOrg(false);
                if (installation.isOnboarded) {
                    await beanieService.sendOutboundConnectionDisconnectedEvent(
                        installation.integrationType,
                        organisation.id,
                        user
                    );
                } else {
                    await beanieService.sendOutboundConnectionAbandonedEvent(
                        installation.integrationType,
                        organisation.id,
                        user
                    );
                }
            })
            .catch((error) => {
                setBackendError(error);
            });
    };

    const onOrgSwitcherCancelClick = async () => {
        if (!installation.isOnboarded) {
            await dispatch(deleteInstallationAction(false)).catch((error) => {
                setBackendError(error);
            });
            await beanieService.sendOutboundConnectionAbandonedEvent(
                installation.integrationType,
                organisation.id,
                user
            );
        }
        clearOAuthCode();
    };

    const clearOAuthCode = () => {
        // need to clear oAuthCode from url when cancelling out of org switcher
        window.location.href = user.tenantId
            ? `${window.location.href.split('?')[0]}?tenantId=${user.tenantId}`
            : window.location.href.split('?')[0];
        setIsInvalidOrg(false);
    };

    const onUpdateAppClick = async () => {
        if (installation.versionSupport!.updateRequiresAuthorization && installation.authorizeUrl) {
            window.location.href = installation.authorizeUrl;
        } else {
            await dispatch(updateInstallationVersionAction());
            await dispatch(getBootstrapAction());
            await setIsEditingSettings(true);
        }
    };

    const onSettingsCancel = () => {
        setIsEditingSettings(false);
        setIsSyncing(false);
    };

    const onSettingsClick = () => {
        setIsEditingSettings(true);
    };

    const setIsInvalidOrg = (isInvalidOrg: boolean) => {
        if (isInvalidOrg) {
            window.localStorage.setItem(localStorageKeys.CONTEXT_ORG, user.tenantId!);
        } else {
            window.localStorage.removeItem(localStorageKeys.CONTEXT_ORG);
        }
        setIsValidatingOrg(false);
    };

    const updateTenant = async (newOrganisation: IOrganisation) => {
        setIsUpdatingTenant(true);

        dispatch(setOrganisation(newOrganisation));
        dispatch(
            updateUser({
                ...user,
                tenantId: newOrganisation.id,
            })
        );

        await postConnection(newOrganisation);

        if (!backendError) {
            dispatch(getBootstrapAction());
            await initHeaderAsync();
        }

        setIsUpdatingTenant(false);
        setIsValidatingOrg(true);
    };

    const onGoBack = useCallback(() => {
        if (!installation.isOnboarded) {
            dispatch(deleteInstallationAction(false)).then(() => window.location.reload());
        }
    }, [dispatch, installation.isOnboarded]);

    const goBackButton = React.useMemo(
        () => ({
            onClick: onGoBack,
            text: lang.ERROR.GO_BACK,
        }),
        [onGoBack]
    );

    const renderError = () => {
        return (
            <ErrorComponent
                description={
                    <div>
                        {lang.ERROR.GO_BACK_WAIT}
                        <Link displayLinkIcon={true} href={lang.ERROR.FIND_AN_ANSWER_LINK}>
                            {lang.ERROR.FIND_AN_ANSWER}
                        </Link>
                    </div>
                }
                heading={lang.ERROR.SOMETHING_WENT_WRONG}
                primaryButton={goBackButton}
            />
        );
    };

    const renderAppOrError = () => {
        if (backendError) {
            const error = backendError as FetchError;
            if (error.code && error.code >= 500 && error.code < 600) {
                return renderError();
            }
        }
        return renderApp();
    };

    const renderApp = () => {
        if (isConnecting) {
            return (
                <div className='xui-heading-large xui-text-align-center xui-margin-vertical-5xlarge'>
                    {`${lang.INFORMATION.CONNECTING_TO.replace('integrationName', installation.displayName)} ${
                        lang.INFORMATION.DO_NOT_REFRESH
                    }`}
                    <XUILoader ariaLabel='Loading' />
                </div>
            );
        }
        if (isBootstrapping || isUpdatingTenant || isFetching) {
            return <XUILoader ariaLabel='Loading' />;
        }
        if (!isAllowed) {
            // Shouldn't be here, either not a xero user or not listed on feature service
            removeHeader();
            return <Fatality header={lang.ERROR.FATAL} sub={lang.ERROR.SUB} />;
        }
        return <div className='app'>{renderPage()}</div>;
    };

    return renderAppOrError();
};

export default withLDConsumer()(App);
